import { ReactNode, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumAccess } from '../hooks/usePremiumAccess';
import { useStripe } from '../hooks/useStripe';
import { isRouteEnabled } from '../config/mvpConfig';
import { supabase } from '../lib/supabaseClient';
import ProfileMenu from '../components/ProfileMenu';
import SearchBar from '../components/ui/SearchBar';
import SmartNotifications from '../components/SmartNotifications';
import OfflineIndicator from '../components/ui/OfflineIndicator';
import AccessibilityMenu from '../components/AccessibilityMenu';
import {
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  Crown,
  Settings,
  RotateCcw,
  Download,
  Heart,
  Search,
  Eye,
  Wrench,
  LifeBuoy,
  Compass,
  BookOpen,
  Trophy,
  CheckSquare,
  Calendar,
  Users,
  UserCircle
} from 'lucide-react';
import { BottomNavBar, MobileHeader, MobileMenu } from '../components/navigation';

type DashboardLayoutProps = {
  children: ReactNode;
};

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  // Language support removed - English only
  const { user } = useAuth();
  const { hasPremiumAccess } = usePremiumAccess();
  const { subscribeToPlan } = useStripe();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);

  const baseMenuItems = [
    {
      path: '/dashboard',
      icon: Compass,
      label: 'The Compass'
    },
    {
      path: '/explore',
      icon: Search,
      label: 'Explore'
    },
    {
      path: '/me',
      icon: UserCircle,
      label: 'Me'
    },
    {
      path: '/guides',
      icon: BookOpen,
      label: 'Guides & Lessons'
    },
    {
      path: '/milestones',
      icon: Trophy,
      label: 'Milestones & Progress'
    },
    {
      path: '/next-steps',
      icon: CheckSquare,
      label: 'Next Steps'
    },
    {
      path: '/library',
      icon: Wrench,
      label: 'My Tools & Resources'
    },
    {
      path: '/connections',
      icon: Users,
      label: 'Connections & Stories'
    },
    {
      path: '/upcoming',
      icon: Calendar,
      label: 'Upcoming'
    },
    {
      path: '/reflections',
      icon: Heart,
      label: 'My Reflections'
    },
    {
      path: '/chat',
      icon: MessageSquare,
      label: 'Ask Mini Angel'
    },
    {
      path: '/profile-settings',
      icon: UserCircle,
      label: 'My Profile & Settings'
    },
    {
      path: '/help',
      icon: LifeBuoy,
      label: 'Help'
    },
    // Add debug menu item in development
    ...(process.env.NODE_ENV === 'development' ? [{
      path: '/user-type-test',
      icon: Settings,
      label: 'Debug User Types'
    }] : [])
  ];

  // Filter menu items based on MVP config
  const filteredBaseMenuItems = baseMenuItems.filter(item => isRouteEnabled(item.path));
  
  // Add admin menu item conditionally
  const menuItems = isAdmin
    ? [...filteredBaseMenuItems, {
        path: '/admin',
        icon: Settings,
        label: 'Admin Dashboard'
      }]
    : filteredBaseMenuItems;

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
          return;
        }

        setIsAdmin(data?.role === 'admin');
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  // Set initial collapsed state based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCollapsed(true);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // Determine if we should show back button instead of menu toggle
  const showBackButton = () => {
    const pathsWithBackButton = [
      '/courses/',
      '/steps/',
      '/profile-setup',
      '/subscription',
      '/admin/users/'
    ];

    return pathsWithBackButton.some(path => location.pathname.includes(path));
  };

  // Determine back button destination
  const getBackPath = () => {
    if (location.pathname.includes('/courses/')) {
      return '/journey';
    }
    if (location.pathname.includes('/steps/')) {
      return '/journey';
    }
    if (location.pathname.startsWith('/admin/users/')) {
      return '/admin';
    }
    return '/dashboard';
  };

  // Get custom right content for specific pages
  const getRightContent = () => {
    if (location.pathname === '/chat') {
      return (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsAccessibilityOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title='Accessibility settings'
          >
            <Eye className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => {
              // Clear conversation functionality would go here
              window.dispatchEvent(new CustomEvent('clear-chat'));
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title='Clear conversation'
          >
            <RotateCcw className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => {
              // Export conversation functionality would go here
              window.dispatchEvent(new CustomEvent('export-chat'));
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title='Export conversation'
          >
            <Download className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      );
    }
    
    // For courses and lessons, we might want to add a completion button
    if (location.pathname.includes('/courses/') && location.pathname.includes('/lessons/')) {
      return (
        <button
          onClick={() => {
            // Toggle completion functionality would go here
            window.dispatchEvent(new CustomEvent('toggle-lesson-completion'));
          }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {/* This would be a dynamic icon based on completion status */}
        </button>
      );
    }
    
    // Modern default header buttons
    return (
      <div className="flex items-center space-x-3">
        <button
          onClick={() => setIsSearchOpen(true)}
          className="p-3 rounded-2xl hover:bg-primary-50 transition-all duration-300 group"
          title='Search'
        >
          <Search className="h-5 w-5 text-neutral-600 group-hover:text-primary-600" />
        </button>
        
        
        <button
          onClick={() => setIsAccessibilityOpen(true)}
          className="p-3 rounded-2xl hover:bg-secondary-50 transition-all duration-300 group"
          title='Accessibility settings'
        >
          <Eye className="h-5 w-5 text-neutral-600 group-hover:text-secondary-600" />
        </button>
        
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-primary-50 to-secondary-50">
      {/* Desktop Sidebar - Visible only on md and larger screens */}
      <aside
        className={`hidden md:flex flex-col fixed top-0 left-0 bottom-0 bg-white/90 backdrop-blur-sm border-r border-neutral-200/50 z-50 transition-all duration-300 ease-in-out shadow-2xl ${
          isCollapsed ? 'w-16' : 'w-64'
        }`}
        style={{ height: '100vh', maxHeight: '100vh' }}
      >
        {/* Modern Sidebar Header with Logo */}
        <div className="h-16 border-b border-neutral-200/50 flex items-center justify-center bg-gradient-to-r from-primary-50 to-secondary-50">
          {isCollapsed ? (
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center">
              <img src="/icons/logo.svg" alt="MyNewStart" className="h-6 w-6" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center">
                <img src="/icons/logo.svg" alt="MyNewStart" className="h-6 w-6" />
              </div>
              <span className="text-h4 text-neutral-800 font-bold">MyNewStart</span>
            </div>
          )}
        </div>

        {/* Modern Collapse/Expand Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute top-16 -right-3 bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full p-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-neutral-600" />
          ) : (
            <ChevronLeft className="h-4 w-4 text-neutral-600" />
          )}
        </button>

        {/* Modern Navigation Menu */}
        <nav className="flex-1 p-4 overflow-y-auto overflow-x-hidden hide-scrollbar min-h-0">
          <div className="space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (location.pathname.startsWith(`${item.path}/`) && item.path !== '/') ||
                (item.path === '/guides' && (location.pathname.startsWith('/journey') || location.pathname.startsWith('/library') || location.pathname.startsWith('/guide'))) ||
                (item.path === '/milestones' && (location.pathname.startsWith('/achievements') || location.pathname.startsWith('/analytics'))) ||
                (item.path === '/library' && location.pathname.startsWith('/toolkit'));
                
              return (
                <button
                  key={item.path}
                  onClick={() => handleMenuItemClick(item.path)}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center' : ''} px-4 py-3 rounded-2xl transition-all duration-300 text-left group ${
                    isActive
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                      : 'text-neutral-700 hover:bg-gradient-to-r hover:from-primary-50 hover:to-secondary-50 hover:shadow-md hover:-translate-y-0.5'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <item.icon className={`h-5 w-5 flex-shrink-0 transition-colors ${
                    isActive ? 'text-white' : 'text-neutral-500 group-hover:text-primary-600'
                  }`} />
                  {!isCollapsed && (
                    <span className={`ml-3 font-medium truncate transition-colors ${
                      isActive ? 'text-white' : 'text-neutral-700 group-hover:text-primary-600'
                    }`}>{item.label}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Modern Premium Upgrade Button */}
          {!isCollapsed && !hasPremiumAccess && (
            <div className="mt-6 pt-6 border-t border-neutral-200/50">
              <button
                onClick={() => subscribeToPlan('monthly')}
                className="w-full flex items-center px-4 py-3 bg-gradient-to-r from-secondary-500 to-accent-500 text-white rounded-2xl hover:from-secondary-600 hover:to-accent-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Crown className="h-5 w-5 mr-3 flex-shrink-0" />
                <span className="font-medium">
                  Upgrade to Premium
                </span>
              </button>
            </div>
          )}
        </nav>

        {/* Modern Sidebar Footer with Profile */}
        <div
          className="mt-auto border-t border-neutral-200/30 px-4 py-3 bg-gradient-to-r from-neutral-50 to-primary-50 flex-shrink-0"
          style={{ minHeight: '72px' }}
        >
          <ProfileMenu
            isOpen={isProfileMenuOpen}
            onToggle={toggleProfileMenu}
            isCollapsed={isCollapsed}
          />
        </div>
      </aside>

      {/* Modern Mobile Overlay - Hidden since we have bottom navigation */}
      {/* {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
          onClick={toggleMobileMenu}
          aria-hidden="true"
        />
      )} */}

      {/* Modern Main Content Area */}
      <div className={`flex flex-col min-w-0 transition-all duration-300 ${isCollapsed ? 'md:ml-16' : 'md:ml-64'}`} style={{ height: '100vh', maxHeight: '100vh' }}>
        {/* Mobile Header - Hidden since we have bottom navigation */}
        {/* {!location.pathname.includes('/lessons/') && (
        <div className="md:hidden">
          <MobileHeader
            onMenuToggle={toggleMobileMenu}
            showBackButton={showBackButton()}
            backPath={getBackPath()}
            rightContent={getRightContent()}
          />
        </div>
      )} */}

        {/* Mobile Menu - Hidden since we have bottom navigation */}
        {/* <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          menuItems={menuItems}
        /> */}

        {/* Modern Desktop Header */}
        <div className={`hidden md:flex items-center justify-end h-16 px-8 bg-transparent ${
          location.pathname.includes('/lessons/') ? 'md:hidden' : ''
        }`}>
          <div className="flex items-center space-x-4">
            {getRightContent()}
            {/* Smart Notifications */}
            <SmartNotifications 
              userId={user?.id}
              onNotificationClick={(notification) => {
                console.log('Notification clicked:', notification);
                // Handle notification click - could navigate to actionUrl
              }}
            />
          </div>
        </div>

        {/* Page Content */}
        <main 
          id="main-content"
          className={`flex-1 min-w-0 overflow-y-auto overflow-x-hidden hide-scrollbar [scrollbar-gutter:stable] ${
            location.pathname.includes('/lessons/') 
              ? 'p-0 pt-0' 
              : 'p-4 md:p-6 lg:p-8 pb-safe pt-0 md:pt-6'
          }`}
          style={{ maxHeight: '100vh', height: '100%' }}
          role="main"
          aria-label="Main content"
        >
          <div className={`w-full min-w-0 ${location.pathname.includes('/lessons/') ? '' : 'max-w-7xl mx-auto'}`}>
            {/* Main Content */}
            {children}
          </div>
        </main>

        {/* Mobile Bottom Navigation  (Visible only on mobile) */}
        <BottomNavBar />
      </div>
      
      {/* Global UI Components */}
      <OfflineIndicator />
      
      {/* Modern Search Modal */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center pt-20 px-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl max-w-2xl w-full border border-white/20">
            <div className="p-6">
              <SearchBar 
                autoFocus={true}
                onResultClick={() => setIsSearchOpen(false)}
              />
              <button
                onClick={() => setIsSearchOpen(false)}
                className="mt-6 w-full text-center text-neutral-600 hover:text-neutral-800 py-3 rounded-2xl hover:bg-neutral-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Accessibility Menu */}
      <AccessibilityMenu 
        isOpen={isAccessibilityOpen}
        onClose={() => setIsAccessibilityOpen(false)}
      />
      
    </div>
  );
};

export default DashboardLayout;